/**
 * 词条格式化：ICU MessageFormat 的子集（spec-core §1.9 I3、附录 A D16：自行实现，不引入第三方库）。
 *
 * 支持的写法：
 * - 变量 `{name}`（名称为字母、数字、下划线，不以数字开头）；
 * - 复数 `{name, plural, =0 {…} one {…} other {…}}`：先按 `=N` 精确匹配，再按 Intl.PluralRules 取类别，
 *   必须有 `other`；分支内的 `#` 显示该数字（英语单复数，PRD 5.14 A45「英语单复数」）；
 * - 参数变量 `{param:<key>}`：只能引用用户端可见的经营参数（I3、PRD 5.14.4），由调用方把当前值解析成文本，
 *   用户端拿到的文案里不会出现参数 key。
 *
 * 不支持 select、number / date 子格式与撇号转义：时间、金额等由调用方按 §7 截断并按语言格式化后作为字符串传入。
 * 变量值可以是 `I18nText = { key, vars }`（spec-core §4.4、D-039），渲染时按当前语言再查一次词条；
 * I18nText 数组逐项渲染后按行连接。
 */
import { formatDecimal } from '@nexora/shared';
import { numberSymbols } from './format.js';
import { LOCALE_TAGS, type Lang } from './langs.js';

/** 渲染时按当前语言解析的词条引用（spec-core §4.4）。 */
export interface I18nText {
  readonly key: string;
  readonly vars?: Readonly<Record<string, MessageValue>>;
}

export type MessageScalar = string | number | boolean | null | undefined;
export type MessageValue = MessageScalar | I18nText | readonly I18nText[];
export type MessageValues = Readonly<Record<string, MessageValue>>;

export type MessageNode =
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'var'; readonly name: string }
  | { readonly kind: 'param'; readonly key: string }
  | { readonly kind: 'pound' }
  | { readonly kind: 'plural'; readonly name: string; readonly cases: readonly PluralCase[] };

export interface PluralCase {
  /** `=N`、zero、one、two、few、many、other */
  readonly selector: string;
  readonly nodes: readonly MessageNode[];
}

export class MessageSyntaxError extends Error {
  constructor(
    message: string,
    readonly offset: number,
  ) {
    super(`${message} at ${offset}`);
    this.name = 'MessageSyntaxError';
  }
}

const NAME_START = /[A-Za-z_]/;
const NAME_CHAR = /[A-Za-z0-9_]/;
const PARAM_KEY_CHAR = /[A-Za-z0-9_.]/;
const PLURAL_CATEGORIES = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);
const PARAM_PREFIX = 'param:';

class Parser {
  private pos = 0;

  constructor(private readonly src: string) {}

  parse(): MessageNode[] {
    const nodes = this.nodes(false);
    if (this.pos < this.src.length) throw new MessageSyntaxError('Unexpected "}"', this.pos);
    return nodes;
  }

  /** 读一段消息，直到字符串结束或遇到（不消费的）`}`。 */
  private nodes(inPlural: boolean): MessageNode[] {
    const out: MessageNode[] = [];
    let text = '';
    const flush = () => {
      if (text) out.push({ kind: 'text', value: text });
      text = '';
    };
    while (this.pos < this.src.length) {
      const ch = this.src[this.pos]!;
      if (ch === '}') break;
      if (ch === '{') {
        flush();
        out.push(this.placeholder());
        continue;
      }
      if (ch === '#' && inPlural) {
        flush();
        out.push({ kind: 'pound' });
        this.pos += 1;
        continue;
      }
      text += ch;
      this.pos += 1;
    }
    flush();
    return out;
  }

  private placeholder(): MessageNode {
    const start = this.pos;
    this.pos += 1; // {
    this.ws();
    if (this.src.startsWith(PARAM_PREFIX, this.pos)) {
      this.pos += PARAM_PREFIX.length;
      const keyStart = this.pos;
      while (this.pos < this.src.length && PARAM_KEY_CHAR.test(this.src[this.pos]!)) this.pos += 1;
      const key = this.src.slice(keyStart, this.pos);
      if (!key || key.startsWith('.') || key.endsWith('.')) throw new MessageSyntaxError('Invalid parameter key', keyStart);
      this.ws();
      this.expect('}');
      return { kind: 'param', key };
    }
    const name = this.name();
    this.ws();
    if (this.peek() === '}') {
      this.pos += 1;
      return { kind: 'var', name };
    }
    this.expect(',');
    this.ws();
    const type = this.name();
    if (type !== 'plural') throw new MessageSyntaxError(`Unsupported argument type "${type}"`, start);
    this.ws();
    this.expect(',');
    const cases: PluralCase[] = [];
    const seen = new Set<string>();
    for (;;) {
      this.ws();
      if (this.peek() === '}') {
        this.pos += 1;
        break;
      }
      if (this.pos >= this.src.length) throw new MessageSyntaxError('Unclosed plural', start);
      const selector = this.selector();
      if (seen.has(selector)) throw new MessageSyntaxError(`Duplicate plural case "${selector}"`, this.pos);
      seen.add(selector);
      this.ws();
      this.expect('{');
      const nodes = this.nodes(true);
      this.expect('}');
      cases.push({ selector, nodes });
    }
    if (!seen.has('other')) throw new MessageSyntaxError('Plural needs an "other" case', start);
    return { kind: 'plural', name, cases };
  }

  private selector(): string {
    if (this.peek() === '=') {
      const start = this.pos;
      this.pos += 1;
      while (this.pos < this.src.length && /[0-9]/.test(this.src[this.pos]!)) this.pos += 1;
      const value = this.src.slice(start, this.pos);
      if (value.length < 2) throw new MessageSyntaxError('Invalid exact plural selector', start);
      return value;
    }
    const start = this.pos;
    const word = this.name();
    if (!PLURAL_CATEGORIES.has(word)) throw new MessageSyntaxError(`Unknown plural category "${word}"`, start);
    return word;
  }

  private name(): string {
    const start = this.pos;
    if (this.pos >= this.src.length || !NAME_START.test(this.src[this.pos]!)) {
      throw new MessageSyntaxError('Expected a name', start);
    }
    this.pos += 1;
    while (this.pos < this.src.length && NAME_CHAR.test(this.src[this.pos]!)) this.pos += 1;
    return this.src.slice(start, this.pos);
  }

  private ws(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos]!)) this.pos += 1;
  }

  private peek(): string | undefined {
    return this.src[this.pos];
  }

  private expect(ch: string): void {
    if (this.src[this.pos] !== ch) throw new MessageSyntaxError(`Expected "${ch}"`, this.pos);
    this.pos += 1;
  }
}

const parseCache = new Map<string, readonly MessageNode[]>();
const PARSE_CACHE_MAX = 5000;

/** 解析词条文本；语法错误抛 MessageSyntaxError。结果按文本缓存。 */
export function parseMessage(source: string): readonly MessageNode[] {
  const cached = parseCache.get(source);
  if (cached) return cached;
  const nodes = new Parser(source).parse();
  if (parseCache.size >= PARSE_CACHE_MAX) parseCache.clear();
  parseCache.set(source, nodes);
  return nodes;
}

/** 语法是否合法。 */
export function isValidMessage(source: string): boolean {
  try {
    parseMessage(source);
    return true;
  } catch {
    return false;
  }
}

export interface MessageVariables {
  /** 变量名（含复数变量），按字母排序 */
  readonly vars: readonly string[];
  /** 引用的参数 key，按字母排序 */
  readonly params: readonly string[];
}

function collect(nodes: readonly MessageNode[], vars: Set<string>, params: Set<string>): void {
  for (const node of nodes) {
    if (node.kind === 'var') vars.add(node.name);
    else if (node.kind === 'param') params.add(node.key);
    else if (node.kind === 'plural') {
      vars.add(node.name);
      for (const c of node.cases) collect(c.nodes, vars, params);
    }
  }
}

/** 取出一段文本使用的变量与参数引用；语法错误抛 MessageSyntaxError。 */
export function messageVariables(source: string): MessageVariables {
  const vars = new Set<string>();
  const params = new Set<string>();
  collect(parseMessage(source), vars, params);
  return { vars: [...vars].sort(), params: [...params].sort() };
}

/**
 * 变量签名：变量名与 `param:<key>` 合在一起排序，用于「四语变量集合必须一致」（I3）的比较，
 * 也是 i18n_entries.variables 的取值。
 */
export function variableSignature(source: string): string[] {
  const { vars, params } = messageVariables(source);
  return [...vars, ...params.map((p) => `${PARAM_PREFIX}${p}`)].sort();
}

export type VariableCheck =
  | { readonly ok: true; readonly variables: readonly string[] }
  | { readonly ok: false; readonly reason: 'syntax'; readonly langs: readonly Lang[] }
  | { readonly ok: false; readonly reason: 'vars'; readonly langs: readonly Lang[]; readonly byLang: Readonly<Partial<Record<Lang, readonly string[]>>> };

/**
 * 四语变量一致性检查（PRD 5.14 A45「变量（四语必须一致，否则不能发布）」）。
 * 只比较给出的语言；以中文源文为基准（没有中文时以第一个语言为基准），列出不一致的语言。
 */
export function checkVariables(values: Readonly<Partial<Record<Lang, string>>>): VariableCheck {
  const byLang: Partial<Record<Lang, string[]>> = {};
  const syntax: Lang[] = [];
  for (const [lang, text] of Object.entries(values) as [Lang, string | undefined][]) {
    if (text === undefined) continue;
    try {
      byLang[lang] = variableSignature(text);
    } catch {
      syntax.push(lang);
    }
  }
  if (syntax.length > 0) return { ok: false, reason: 'syntax', langs: syntax };
  const langs = Object.keys(byLang) as Lang[];
  if (langs.length === 0) return { ok: true, variables: [] };
  const baseLang: Lang = byLang.zh ? 'zh' : langs[0]!;
  const base = byLang[baseLang]!;
  const differ = langs.filter((lang) => !sameList(byLang[lang]!, base));
  if (differ.length > 0) return { ok: false, reason: 'vars', langs: differ, byLang };
  return { ok: true, variables: base };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export interface FormatMessageOptions {
  lang: Lang;
  vars?: MessageValues | undefined;
  /** `{param:<key>}` 的当前值文本；返回 null / undefined 时显示为空 */
  param?: ((key: string) => string | null | undefined) | undefined;
  /** 渲染 I18nText 变量（按当前语言查词条） */
  text?: ((key: string, vars: MessageValues | undefined) => string) | undefined;
}

const pluralRulesCache = new Map<Lang, Intl.PluralRules>();

function pluralRules(lang: Lang): Intl.PluralRules {
  let rules = pluralRulesCache.get(lang);
  if (!rules) {
    rules = new Intl.PluralRules(LOCALE_TAGS[lang]);
    pluralRulesCache.set(lang, rules);
  }
  return rules;
}

const NUMERIC = /^-?\d+(\.\d+)?$/;
const INTEGER = /^-?\d+$/;

function isI18nText(value: unknown): value is I18nText {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && typeof (value as I18nText).key === 'string';
}

function renderValue(value: MessageValue, options: FormatMessageOptions): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return (value as readonly I18nText[]).map((item) => renderValue(item, options)).filter((s) => s !== '').join('\n');
  }
  if (isI18nText(value)) return options.text ? options.text(value.key, value.vars) : '';
  return '';
}

/** `#` 的显示：整数按语言加千分位，其余原样显示（金额等已由调用方截断格式化）。 */
function renderPound(value: MessageValue, lang: Lang): string {
  const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (INTEGER.test(raw)) return formatDecimal(raw, 0, numberSymbols(lang));
  return raw;
}

function pickCase(node: Extract<MessageNode, { kind: 'plural' }>, value: MessageValue, lang: Lang): PluralCase {
  const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  const byName = (selector: string) => node.cases.find((c) => c.selector === selector);
  if (NUMERIC.test(raw)) {
    const exact = node.cases.find((c) => c.selector.startsWith('=') && Number(c.selector.slice(1)) === Number(raw));
    if (exact) return exact;
    // 只用于选择复数类别，不参与任何金额计算
    const category = pluralRules(lang).select(Number(raw));
    const hit = byName(category);
    if (hit) return hit;
  }
  return byName('other')!;
}

function renderNodes(nodes: readonly MessageNode[], options: FormatMessageOptions, pound: MessageValue | undefined): string {
  let out = '';
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        out += node.value;
        break;
      case 'var':
        out += renderValue(options.vars?.[node.name], options);
        break;
      case 'param':
        out += options.param?.(node.key) ?? '';
        break;
      case 'pound':
        out += pound === undefined ? '#' : renderPound(pound, options.lang);
        break;
      case 'plural': {
        const value = options.vars?.[node.name];
        const chosen = pickCase(node, value, options.lang);
        out += renderNodes(chosen.nodes, options, value);
        break;
      }
    }
  }
  return out;
}

/**
 * 渲染词条文本。未提供的变量显示为空（不显示占位符与变量名）；
 * 语法错误时返回空串，避免把占位符或参数 key 显示给用户（发布前检查会拦下语法错误）。
 */
export function formatMessage(source: string, options: FormatMessageOptions): string {
  let nodes: readonly MessageNode[];
  try {
    nodes = parseMessage(source);
  } catch {
    return '';
  }
  return renderNodes(nodes, options, undefined);
}
